import { useTheme } from '@mui/material';
import { useMemo } from 'react';

import { createColumnHelper } from '@tanstack/react-table';

import { AvatarCell } from '../../../components/Table/CellRenderers/AvatarCell';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { Typography } from '../../../components/Typography/Typography';
import { MonthlyTimesheetRow } from '../../../types';
import TotalCell from '../components/TotalCell';
import { useAtomValue } from 'jotai';
import { isSmallScreenState, userState } from '../../../state/UIState';

const columnHelper = createColumnHelper<MonthlyTimesheetRow>();

export const useDealOwnersSingleTableColumns = () => {
  const { colors } = useTheme();
  const user = useAtomValue(userState);
  const isSmallScreen = useAtomValue(isSmallScreenState);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        cell: (info) => {
          const timesheet = info.row.original;
          const isFixedDeal = timesheet.dealId === 0;
          // Highlight rows this user is an EFFECTIVE approver of (owners minus delegated-away, plus
          // delegated-to), mirroring the table's row filter; fall back to raw ownership when the
          // backend didn't attach effective approvers (no delegations in play).
          const highlighted = timesheet.dealOwners
            ? timesheet.dealOwners.some((approver) => approver.id === user?.id)
            : user?.dealOwnerDeals?.some((deal) => deal.id === timesheet.dealId);

          return (
            <AvatarCell
              name={info.row.original.name}
              avatarBoxStyle={{
                background: isFixedDeal
                  ? colors.surfaceBackground.highlighted
                  : colors.surfaceIndicator.needsReview,
              }}
              avatarNameStyle={{
                color: highlighted ? colors.text.main : colors.text.caption,
              }}
              avatarLetterStyle={{
                color: colors.text.main,
              }}
            />
          );
        },
        enableSorting: false,

        header: () => <TableHeaderCell text='Deal' />,
        footer: () => {
          return (
            <Typography
              variant='body'
              color={colors.text.main}
              style={{
                paddingLeft: '30px',
                fontSize: '17px',
              }}
            >
              Total per Month
            </Typography>
          );
        },
        meta: {
          tdStyles: { paddingLeft: '16px', borderRight: `none` },
          thStyles: { borderRight: `none`, paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('avg', {
        cell: (info) => {
          const timesheet = info.row.original;
          const totalPerDeal = timesheet.avg?.toFixed() ?? 0;

          const highlighted = timesheet.dealOwners
            ? timesheet.dealOwners.some((approver) => approver.id === user?.id)
            : user?.dealOwnerDeals?.some((deal) => deal.id === timesheet.dealId);
          return (
            <Typography
              variant='body'
              color={colors.text.main}
              style={{
                textWrap: 'wrap',
                color: highlighted ? colors.text.main : colors.text.caption,
              }}
            >
              {`${totalPerDeal}%`}
            </Typography>
          );
        },
        header: () => <TableHeaderCell text='Total per deal' />,
        footer: TotalCell,

        enableSorting: false,

        meta: {
          width: isSmallScreen ? '100px' : '20%',
          maxWidth: isSmallScreen ? '100px' : '20%',
          minWidth: isSmallScreen ? '100px' : '20%',
          tdStyles: { paddingLeft: '16px', paddingRight: '16px', borderLeft: `none` },
          thStyles: { borderRight: `none`, paddingRight: '16px', borderLeft: `none` },
        },
      }),
    ],
    [colors.surfaceBackground.highlighted, colors.surfaceIndicator.needsReview, colors.text.caption, colors.text.main, isSmallScreen, user?.dealOwnerDeals, user?.id]
  );

  return columns;
};
